import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { useMobileDetection, MobileButton } from "@/components/ui/enhanced-mobile";
import { cn } from "@/lib/utils";
import aureumLogo from "@/assets/aureum-logo-new.png";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useI18n } from "@/i18n";
import { ChevronDown } from "lucide-react";
import { SiteSearch } from "@/components/SiteSearch";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const location = useLocation();
  const { isMobile } = useMobileDetection();
  const { t, language } = useI18n();
  
  const isHomePage = location.pathname === `/${language}` || location.pathname === `/${language}/`;

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      requestAnimationFrame(() => {
        const navHeight = isMobile ? 80 : 100;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY - navHeight;
        
        requestAnimationFrame(() => {
          window.scrollTo({
            top: elementPosition,
            behavior: "smooth"
          });
        });
      });
    }
  };

  // Product submenu items
  const productItems = [
    { name: "LUXA", href: "/products/luxa", descKey: "luxa" as const },
    { name: "LUNA", href: "/products/luna", descKey: "luna" as const },
    { name: "NOVA", href: "/products/nova", descKey: "nova" as const },
    { name: "ARIA", href: "/products/aria", descKey: "aria" as const },
    { name: "VENA", href: "/products/vena", descKey: "vena" as const },
    { name: "AURELIUS", href: "/products/aurelius", descKey: "aurelius" as const },
    { name: "AURELUX", href: "/products/aurelux", descKey: "aurelux" as const },
    { name: "LUMA", href: "/products/luma", descKey: "luma" as const },
  ];

  return (
    <nav className={cn(
      "fixed top-0 w-full z-50 transition-all duration-500",
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md border-b border-accent/20 shadow-md' 
        : 'bg-gradient-to-b from-black/40 via-black/20 to-transparent backdrop-blur-[2px]'
    )}>
      <div className="container mx-auto px-6 lg:px-12">
        <div className={cn(
          "flex items-center justify-between transition-all duration-300",
          isScrolled ? (isMobile ? 'h-20' : 'h-24') : (isMobile ? 'h-24' : 'h-28')
        )}>
          <div className="flex items-center">
            <LocalizedLink to="/" aria-label="Aureum VII Trading Home" className="block">
              <img 
                src={aureumLogo} 
                alt="Aureum VII Trading – REM7 Smart Toilets" 
                className={cn(
                  "transition-all duration-500 w-auto",
                  isScrolled ? 'opacity-100 drop-shadow-sm' : 'opacity-100 drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]',
                  isMobile
                    ? (isScrolled ? 'h-[48px]' : 'h-[56px]')
                    : (isScrolled ? 'h-[64px]' : 'h-[80px]')
                )}
                width="320"
                height="80"
                loading="eager"
              />
            </LocalizedLink>
          </div>
          
          {/* Desktop Navigation - Simplified 5 items */}
          <div className="hidden lg:flex items-center space-x-5 xl:space-x-7">
            {/* Home */}
            {isHomePage ? (
              <button
                onClick={() => scrollToSection("home")}
                className={cn(
                  "whitespace-nowrap hover:text-accent transition-all duration-300 font-inter font-medium hover:scale-105",
                  isScrolled ? 'text-muted-foreground' : 'text-white/90'
                )}
              >
                {t.nav.home}
              </button>
            ) : (
              <LocalizedLink
                to="/"
                className={cn(
                  "whitespace-nowrap hover:text-accent transition-all duration-300 font-inter font-medium hover:scale-105",
                  isScrolled ? 'text-muted-foreground' : 'text-white/90'
                )}
              >
                {t.nav.home}
              </LocalizedLink>
            )}
            
            {/* Products Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setIsProductsOpen(true)}
              onMouseLeave={() => setIsProductsOpen(false)}
            >
              <button
                className={cn(
                  "flex items-center gap-1 hover:text-accent transition-all duration-300 font-inter font-medium hover:scale-105",
                  isScrolled ? 'text-muted-foreground' : 'text-white/90'
                )}
              >
                {t.nav.products}
                <ChevronDown className={cn("w-4 h-4 transition-transform", isProductsOpen && "rotate-180")} />
              </button>
              
              {/* Dropdown Menu */}
              <div className={cn(
                "absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-border/20 overflow-hidden transition-all duration-200",
                isProductsOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
              )}>
                {/* REM7 Flagship - Featured */}
                <LocalizedLink
                  to="/products/rem7"
                  className="block px-4 py-3 bg-accent/10 hover:bg-accent/20 transition-colors border-b border-border/10"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-inter font-bold text-accent block">REM7</span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-accent text-accent-foreground px-1.5 py-0.5 rounded">{t.nav.flagship}</span>
                  </div>
                  <span className="font-inter text-xs text-muted-foreground">{t.nav.productDescriptions.rem7}</span>
                </LocalizedLink>
                {productItems.map((item) => (
                  <LocalizedLink
                    key={item.name}
                    to={item.href}
                    className="block px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/10 last:border-0"
                  >
                    <span className="font-inter font-semibold text-primary block">{item.name}</span>
                    <span className="font-inter text-xs text-muted-foreground">{t.nav.productDescriptions[item.descKey]}</span>
                  </LocalizedLink>
                ))}
                <LocalizedLink
                  to="/collection"
                  className="block px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors text-center"
                >
                  <span className="font-inter text-sm text-accent font-medium">{t.nav.viewAllProducts} →</span>
                </LocalizedLink>
              </div>
            </div>
            
            {/* Bath & Sanitary */}
            <LocalizedLink
              to="/bath-sanitary"
              className={cn(
                "whitespace-nowrap hover:text-accent transition-all duration-300 font-inter font-medium hover:scale-105",
                isScrolled ? 'text-muted-foreground' : 'text-white/90'
              )}
            >
              Bath & Sanitary
            </LocalizedLink>

            {/* Technology */}
            <LocalizedLink to="/business" className={cn(
                "whitespace-nowrap hover:text-accent transition-all duration-300 font-inter font-medium hover:scale-105",
                isScrolled ? 'text-muted-foreground' : 'text-white/90'
              )}>
              Trade &amp; Projects
            </LocalizedLink>
            <LocalizedLink to="/blog" className={cn(
                "whitespace-nowrap hover:text-accent transition-all duration-300 font-inter font-medium hover:scale-105",
                isScrolled ? 'text-muted-foreground' : 'text-white/90'
              )}>
              Resources
            </LocalizedLink>
            <LocalizedLink to="/trade-brochure" className={cn(
                "whitespace-nowrap hover:text-accent transition-all duration-300 font-inter font-medium hover:scale-105",
                isScrolled ? 'text-muted-foreground' : 'text-white/90'
              )}>
              Brochure
            </LocalizedLink>
            
            {/* About */}
            <LocalizedLink
              to="/about"
              className={cn(
                "whitespace-nowrap hover:text-accent transition-all duration-300 font-inter font-medium hover:scale-105",
                isScrolled ? 'text-muted-foreground' : 'text-white/90'
              )}
            >
              {t.nav.about}
            </LocalizedLink>
            
            {/* Contact */}
            <LocalizedLink
              to="/contact"
              className={cn(
                "whitespace-nowrap hover:text-accent transition-all duration-300 font-inter font-medium hover:scale-105",
                isScrolled ? 'text-muted-foreground' : 'text-white/90'
              )}
            >
              {t.nav.contact}
            </LocalizedLink>
            
            <SiteSearch className={isScrolled ? 'text-muted-foreground' : 'text-white/90'} />
            <LocalizedLink to="/contact?intent=trade#enquiry-form">
              <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-inter font-semibold px-5 py-2.5 rounded-lg shadow-gold transition-all hover:scale-105">
                Request Trade Pricing
              </Button>
            </LocalizedLink>
            <LanguageSwitcher variant="default" />
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center gap-2">
            <SiteSearch className={isScrolled ? 'text-foreground' : 'text-white'} />
            <LanguageSwitcher variant="compact" />
            <MobileButton 
              variant="ghost" 
              className={cn(
                "p-2 transition-colors duration-300",
                isScrolled ? 'text-foreground hover:text-accent' : 'text-white hover:text-white/80'
              )}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </MobileButton>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className={cn(
              "lg:hidden absolute top-full left-0 w-full z-40 transform transition-all duration-300",
              "bg-white/95 backdrop-blur-md border-b border-border/20 shadow-sm",
              isMobileMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"
            )}
          >
            <div className="container mx-auto px-6 py-6 space-y-2 max-h-[70vh] overflow-y-auto ios-scroll-fix">
              {/* Home */}
              {isHomePage ? (
                <MobileButton
                  onClick={() => scrollToSection("home")}
                  className="w-full text-left text-foreground hover:text-accent transition-colors font-inter font-medium justify-start p-3 h-auto bg-transparent"
                >
                  {t.nav.home}
                </MobileButton>
              ) : (
                <LocalizedLink
                  to="/"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-left text-foreground hover:text-accent transition-colors font-inter font-medium py-3 px-3 rounded-md hover:bg-accent/10 touch-manipulation"
                >
                  {t.nav.home}
                </LocalizedLink>
              )}
              
              {/* Products Section */}
              <div className="border-t border-border/10 pt-2">
                <span className="block px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {t.nav.products}
                </span>
                <LocalizedLink
                  to="/products/rem7"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-left text-accent font-inter font-bold py-3 px-6 rounded-md bg-accent/10 hover:bg-accent/20 touch-manipulation"
                >
                  REM7 <span className="text-[10px] uppercase tracking-wider bg-accent text-accent-foreground px-1.5 py-0.5 rounded ml-1">{t.nav.flagship}</span>
                  <span className="block text-xs text-muted-foreground font-normal">{t.nav.productDescriptions.rem7}</span>
                </LocalizedLink>
                {productItems.map((item) => (
                  <LocalizedLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-left text-foreground hover:text-accent transition-colors font-inter font-medium py-3 px-6 rounded-md hover:bg-accent/10 touch-manipulation"
                  >
                    <span className="block font-bold text-foreground">{item.name}</span>
                    <span className="block text-xs text-muted-foreground">{t.nav.productDescriptions[item.descKey]}</span>
                  </LocalizedLink>
                ))}
                <LocalizedLink
                  to="/collection"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block w-full text-left text-accent hover:text-accent/80 transition-colors font-inter font-medium py-3 px-6 rounded-md hover:bg-accent/10 touch-manipulation"
                >
                  {t.nav.viewAllProducts} →
                </LocalizedLink>
              </div>
              
              {/* Bath & Sanitary */}
              <LocalizedLink
                to="/bath-sanitary"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left text-foreground hover:text-accent transition-colors font-inter font-medium py-3 px-3 rounded-md hover:bg-accent/10 touch-manipulation"
              >
                Bath & Sanitary
              </LocalizedLink>

              {/* Technology */}
              <LocalizedLink to="/business" onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left text-foreground hover:text-accent transition-colors font-inter font-medium py-3 px-3 rounded-md hover:bg-accent/10 touch-manipulation">Trade &amp; Projects</LocalizedLink>
              <LocalizedLink to="/trade-brochure" onClick={() => setIsMobileMenuOpen(false)} className="block w-full text-left text-sm text-muted-foreground hover:text-accent py-2 pl-6 pr-3 touch-manipulation">Trade Brochure</LocalizedLink>
              <LocalizedLink to="/blog" onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left text-foreground hover:text-accent transition-colors font-inter font-medium py-3 px-3 rounded-md hover:bg-accent/10 touch-manipulation">Resources</LocalizedLink>
              <LocalizedLink to="/technology" onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left text-foreground hover:text-accent transition-colors font-inter font-medium py-3 px-3 rounded-md hover:bg-accent/10 touch-manipulation">{t.nav.technology}</LocalizedLink>
              
              {/* About */}
              <LocalizedLink
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left text-foreground hover:text-accent transition-colors font-inter font-medium py-3 px-3 rounded-md hover:bg-accent/10 touch-manipulation"
              >
                {t.nav.about}
              </LocalizedLink>
              
              {/* Contact */}
              <LocalizedLink
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block w-full text-left text-foreground hover:text-accent transition-colors font-inter font-medium py-3 px-3 rounded-md hover:bg-accent/10 touch-manipulation"
              >
                {t.nav.contact}
              </LocalizedLink>
              
              {/* CTA Button */}
              <div className="pt-4 border-t border-border/10">
                <div className="space-y-2"><LocalizedLink to="/contact?intent=trade#enquiry-form" onClick={() => setIsMobileMenuOpen(false)} className="block w-full">
                  <Button className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-inter font-semibold py-3 rounded-lg">Request Trade Pricing</Button>
                </LocalizedLink><LocalizedLink to="/contact?intent=consultation#enquiry-form" onClick={() => setIsMobileMenuOpen(false)} className="block w-full">
                  <Button variant="outline" className="w-full font-inter font-semibold py-3 rounded-lg">Book Consultation</Button>
                </LocalizedLink><LocalizedLink to="/contact?intent=waitlist#enquiry-form" onClick={() => setIsMobileMenuOpen(false)} className="block w-full">
                  <Button variant="outline" className="w-full font-inter font-semibold py-3 rounded-lg">Join Post-Arrival Demo Waitlist</Button>
                </LocalizedLink></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
